/** ตรงกับ admin CloseLotteryNumbers + backend LotteryRound.findMatchingNumberSetting */

export type RoundNumberSetting = {
  number: string
  customPayoutRate?: number | null
  isFull?: boolean
  isRestricted?: boolean
  reverseNumber?: boolean
  notes?: string | null
}

export function getUniqueNumberPermutations(number: string): string[] {
  const digits = String(number || '').split('')
  if (digits.length === 0) return []

  const results = new Set<string>()
  const used = new Array(digits.length).fill(false)
  const current: string[] = []

  const build = () => {
    if (current.length === digits.length) {
      results.add(current.join(''))
      return
    }
    for (let i = 0; i < digits.length; i++) {
      if (used[i]) continue
      used[i] = true
      current.push(digits[i])
      build()
      current.pop()
      used[i] = false
    }
  }

  build()
  return Array.from(results)
}

export function getEffectiveNumbers(number: string, reverseNumber?: boolean): string[] {
  return reverseNumber ? getUniqueNumberPermutations(number) : [number]
}

export function findMatchingNumberSetting(
  numberSettings: RoundNumberSetting[] | undefined,
  targetNumber: string,
): RoundNumberSetting | null {
  if (!Array.isArray(numberSettings) || numberSettings.length === 0) {
    return null
  }

  const exact = numberSettings.find((setting) => setting.number === targetNumber)
  if (exact) return exact

  return (
    numberSettings.find(
      (setting) =>
        setting.reverseNumber === true &&
        getEffectiveNumbers(setting.number, true).includes(targetNumber),
    ) || null
  )
}

export function hasCustomPayoutRateSetting(
  numberSettings: RoundNumberSetting[] | undefined,
  targetNumber: string,
): boolean {
  const setting = findMatchingNumberSetting(numberSettings, targetNumber)
  return setting?.customPayoutRate != null
}

export function getCustomPayoutRateFromSettings(
  numberSettings: RoundNumberSetting[] | undefined,
  targetNumber: string,
): number | null {
  const setting = findMatchingNumberSetting(numberSettings, targetNumber)
  if (setting?.customPayoutRate != null) {
    return setting.customPayoutRate
  }
  return null
}

export type RoundLimitsLike = {
  prizeTypeLimits?: Array<{
    prizeTypeCode: string
    numberSettings?: RoundNumberSetting[]
  }>
}

export function findRoundLimitNumberSetting(
  roundLimits: RoundLimitsLike | undefined,
  prizeTypeCode: string,
  number: string,
): RoundNumberSetting | null {
  if (!roundLimits?.prizeTypeLimits) return null
  const code = prizeTypeCode.toUpperCase()
  const limit = roundLimits.prizeTypeLimits.find((l) => l.prizeTypeCode?.toUpperCase() === code)
  return findMatchingNumberSetting(limit?.numberSettings, number)
}

export function hasCustomPayoutRateForRoundLimit(
  roundLimits: RoundLimitsLike | undefined,
  prizeTypeCode: string,
  number: string,
): boolean {
  const setting = findRoundLimitNumberSetting(roundLimits, prizeTypeCode, number)
  return setting?.customPayoutRate != null
}

export function getCustomPayoutRateForRoundLimit(
  roundLimits: RoundLimitsLike | undefined,
  prizeTypeCode: string,
  number: string,
): number | null {
  const setting = findRoundLimitNumberSetting(roundLimits, prizeTypeCode, number)
  if (setting?.customPayoutRate != null) {
    return setting.customPayoutRate
  }
  return null
}
